import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, Plus, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DriverMaster } from '@/types/driverMaster'
import { Platform } from '@/types'
import { EarningsService } from '@/services/earningsService'
import { generatePeriodFromDate, getPeriodOptions, getCurrentPeriod } from '@/utils/periodHelpers'
import { getUserSelectablePlatforms } from '@/constants/platforms'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

interface ManualEarningsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  drivers: DriverMaster[]
}

type DialogStep = 'options' | 'manual'

export function ManualEarningsDialog({
  open,
  onOpenChange,
  drivers
}: ManualEarningsDialogProps) {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [step, setStep] = useState<DialogStep>('options')
  const [loading, setLoading] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    driverId: '',
    period: getCurrentPeriod(), // Default to current month
    platform: '' as Platform,
    grossEarnings: '',
    netEarnings: '',
    tips: ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleClose = () => {
    setStep('options')
    setFormData({
      driverId: '',
      period: getCurrentPeriod(),
      platform: '' as Platform,
      grossEarnings: '',
      netEarnings: '',
      tips: ''
    })
    setErrors({})
    onOpenChange(false)
  }

  const handleCSVOption = () => {
    handleClose()
    navigate('/upload')
  }

  const handleManualOption = () => {
    setStep('manual')
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.driverId) {
      newErrors.driverId = 'Förare är obligatorisk'
    }
    if (!formData.period) {
      newErrors.period = 'Period är obligatorisk'
    }
    if (!formData.platform) {
      newErrors.platform = 'Plattform är obligatorisk'
    }
    if (!formData.grossEarnings || isNaN(Number(formData.grossEarnings)) || Number(formData.grossEarnings) < 0) {
      newErrors.grossEarnings = 'Bruttointäkter måste vara ett positivt tal'
    }
    if (!formData.netEarnings || isNaN(Number(formData.netEarnings)) || Number(formData.netEarnings) < 0) {
      newErrors.netEarnings = 'Nettointäkter måste vara ett positivt tal'
    }
    if (formData.tips && (isNaN(Number(formData.tips)) || Number(formData.tips) < 0)) {
      newErrors.tips = 'Dricks måste vara ett positivt tal'
    }

    // Validate net <= gross
    if (formData.grossEarnings && formData.netEarnings) {
      const gross = Number(formData.grossEarnings)
      const net = Number(formData.netEarnings)
      if (net > gross) {
        newErrors.netEarnings = 'Nettointäkter kan inte vara högre än bruttointäkter'
      }
    }

    // Validate net >= tips (tips are part of net earnings)
    if (formData.netEarnings && formData.tips) {
      const net = Number(formData.netEarnings)
      const tips = Number(formData.tips)
      if (tips > net) {
        newErrors.tips = 'Nettointäkter måste vara minst lika med dricks (dricks är inkluderat i netto)'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || !currentUser) return

    setLoading(true)
    try {
      const uploadSessionId = `manual_${Date.now()}`

      await EarningsService.createEarning(currentUser, {
        driverId: formData.driverId,
        period: formData.period,
        uploadSessionId,
        platform: formData.platform,
        grossEarnings: Number(formData.grossEarnings),
        netEarnings: Number(formData.netEarnings),
        tips: Number(formData.tips) || 0
      })

      toast.success('Intäktspost skapad framgångsrikt')
      handleClose()
    } catch (error) {
      console.error('Error creating earning:', error)
      toast.error(error instanceof Error ? error.message : 'Det gick inte att skapa intäktsposten')
    } finally {
      setLoading(false)
    }
  }

  // Get platform options from constants
  const platformOptions = getUserSelectablePlatforms()

  // Convert drivers to searchable options
  const driverOptions = drivers.map(driver => ({
    value: driver.id,
    label: driver.primaryName
  }))

  if (step === 'options') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lägg till intäkter</DialogTitle>
            <DialogDescription>Välj hur du vill lägga till intäkter</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Button
              onClick={handleCSVOption}
              className="w-full flex items-center justify-start gap-3"
              variant="outline"
            >
              <Upload className="h-5 w-5 flex-shrink-0" />
              <span className="font-semibold">Jag har en CSV</span>
            </Button>

            <Button
              onClick={handleManualOption}
              className="w-full flex items-center justify-start gap-3"
              variant="outline"
            >
              <Plus className="h-5 w-5 flex-shrink-0" />
              <span className="font-semibold">Lägg till manuellt</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Lägg till intäkter manuellt</DialogTitle>
          <DialogDescription>Fyll i intäktsuppgifter för en förare</DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {Object.keys(errors).length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Vänligen åtgärda felen nedan innan du fortsätter.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="driver">Förare *</Label>
              <Combobox
                options={driverOptions}
                value={formData.driverId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, driverId: value }))}
                placeholder="Sök och välj förare..."
                className={errors.driverId ? 'border-red-500' : ''}
                emptyMessage="Inga förare hittades."
              />
              {errors.driverId && <p className="text-sm text-red-500">{errors.driverId}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="period">Period *</Label>
              <Combobox
                options={getPeriodOptions()}
                value={formData.period}
                onValueChange={(value) => setFormData(prev => ({ ...prev, period: value }))}
                placeholder="Välj period..."
                className={errors.period ? 'border-red-500' : ''}
                emptyMessage="Ingen period hittades."
              />
              {errors.period && <p className="text-sm text-red-500">{errors.period}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="platform">Plattform *</Label>
              <Combobox
                options={platformOptions}
                value={formData.platform}
                onValueChange={(value) => setFormData(prev => ({ ...prev, platform: value as Platform }))}
                placeholder="Sök och välj plattform..."
                className={errors.platform ? 'border-red-500' : ''}
                emptyMessage="Ingen plattform hittades."
              />
              {errors.platform && <p className="text-sm text-red-500">{errors.platform}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="grossEarnings">Bruttointäkter (SEK) *</Label>
                <Input
                  id="grossEarnings"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.grossEarnings}
                  onChange={(e) => setFormData(prev => ({ ...prev, grossEarnings: e.target.value }))}
                  className={errors.grossEarnings ? 'border-red-500' : ''}
                />
                {errors.grossEarnings && <p className="text-sm text-red-500">{errors.grossEarnings}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="netEarnings">Nettointäkter (inkl. dricks) (SEK) *</Label>
                <Input
                  id="netEarnings"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.netEarnings}
                  onChange={(e) => setFormData(prev => ({ ...prev, netEarnings: e.target.value }))}
                  className={errors.netEarnings ? 'border-red-500' : ''}
                />
                {errors.netEarnings && <p className="text-sm text-red-500">{errors.netEarnings}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tips">Dricks (SEK)</Label>
              <Input
                id="tips"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.tips}
                onChange={(e) => setFormData(prev => ({ ...prev, tips: e.target.value }))}
                className={errors.tips ? 'border-red-500' : ''}
              />
              {errors.tips && <p className="text-sm text-red-500">{errors.tips}</p>}
              <p className="text-sm text-muted-foreground">Valfritt - inkluderas redan i nettointäkter ovan</p>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0">
          <div className="flex gap-3 w-full">
            <Button onClick={() => setStep('options')} variant="outline" disabled={loading}>
              Tillbaka
            </Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1">
              {loading ? 'Skapar...' : 'Skapa intäktspost'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}